import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router';
import { ChevronLeft, Pencil, UserPlus } from 'lucide-react';
import {
  INVITABLE_ROLES,
  memberName,
  roleLabel,
  type InvitableRole,
  type TeamMember,
} from '@kratispot/shared';
import { ApiError, fetchTeam, inviteMember, removeMember, updateMember } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Toasts, useToasts } from '@/components/toasts';
import {
  BLANK_DRAFT,
  changesOf,
  draftOf,
  MemberDialog,
  type MemberDraft,
} from '@/components/member-dialog';
import { useTranslation, type CopyKey } from '@/lib/i18n';
import { keptNameNote } from '@/lib/kept-name';
import { cn } from '@/lib/utils';

const STATUS_KEY: Readonly<Record<TeamMember['status'], CopyKey>> = {
  active: 'active',
  invited: 'invited',
  inactive: 'inactive',
};

// §4: an invitation nobody found is still an address the venue meant to admit,
// so an owner may switch it on rather than re-sending into the same silence.
const ACTION_FOR: Readonly<Record<TeamMember['status'], CopyKey>> = {
  active: 'deactivate',
  invited: 'activate',
  inactive: 'reactivate',
};

const STATUS_TONE: Readonly<Record<TeamMember['status'], string>> = {
  active: 'bg-emerald-500',
  invited: 'bg-amber-500',
  inactive: 'bg-muted-foreground',
};

export function initialsOf(member: TeamMember): string {
  return memberName(member)
    .split(/[\s.@_-]+/)
    .filter((part) => part !== '')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

export function TeamPage({ venueId }: { venueId: string }): ReactElement {
  const { t, language } = useTranslation();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const { toasts, push } = useToasts();

  const load = useCallback(
    (signal?: AbortSignal) =>
      fetchTeam(venueId, signal)
        .then((response) => {
          setMembers(response.members);
          setPhase('ready');
        })
        .catch(() => {
          if (!signal?.aborted) setPhase('failed');
        }),
    [venueId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  function replace(updated: TeamMember): void {
    setMembers((current) => current.map((member) => (member.id === updated.id ? updated : member)));
  }

  async function invite(draft: MemberDraft): Promise<void> {
    await attempt('inviteFailed', async () => {
      note(draft.name, await inviteMember(venueId, draft));
      setInviting(false);
      await load();
    });
  }

  // §4: the account's own name stood, so the screen says which one, and why.
  function note(typed: string, member: TeamMember): void {
    const kept = keptNameNote(typed, member.name, t);
    if (kept !== null) push(kept, 'bg-amber-500');
  }

  // A corrected address re-invites, so the answer is the row the API returns.
  async function correct(member: TeamMember, draft: MemberDraft): Promise<void> {
    const update = changesOf(member, draft);
    if (Object.keys(update).length === 0) return setEditing(null);
    await attempt('memberSaveFailed', async () => {
      const saved = await updateMember(venueId, member.id, update);
      replace(saved);
      note(draft.name, saved);
      setEditing(null);
    });
  }

  async function attempt(failure: CopyKey, work: () => Promise<void>): Promise<void> {
    setBusy(true);
    setDialogError(null);
    try {
      await work();
    } catch (error) {
      const duplicate = error instanceof ApiError && error.status === 409;
      setDialogError(t(duplicate ? 'inviteDuplicate' : failure));
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(member: TeamMember, role: InvitableRole): Promise<void> {
    replace(await updateMember(venueId, member.id, { role }));
  }

  async function setActive(member: TeamMember, active: boolean): Promise<void> {
    replace(await updateMember(venueId, member.id, { active }));
  }

  async function drop(member: TeamMember): Promise<void> {
    if (!window.confirm(t('removeConfirm'))) return;
    await removeMember(venueId, member.id);
    setMembers((current) => current.filter((row) => row.id !== member.id));
  }

  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="outline" size="sm">
          <Link to="/">
            <ChevronLeft className="size-4" aria-hidden />
            {t('back')}
          </Link>
        </Button>
        <h2 className="truncate text-base font-bold">{t('team')}</h2>
        <Button size="sm" className="ml-auto" onClick={() => setInviting(true)}>
          <UserPlus className="size-4" aria-hidden />
          {t('invite')}
        </Button>
      </div>

      {phase !== 'ready' ? (
        <p
          role={phase === 'failed' ? 'alert' : undefined}
          className="text-sm text-muted-foreground"
        >
          {t(phase === 'failed' ? 'errorGeneric' : 'loading')}
        </p>
      ) : (
        <ul className="divide-y rounded-xl border bg-card">
          {members.map((member) => (
            <li key={member.id} className="flex flex-wrap items-center gap-3 p-3">
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-accent/15 text-xs font-extrabold text-primary">
                {initialsOf(member)}
              </span>
              {/* A basis, so Greek status words cannot squeeze a name to "Ammos ..." */}
              <span className="min-w-0 flex-1 basis-40">
                <span className="block truncate text-sm font-semibold">{memberName(member)}</span>
                <span className="block truncate text-xs text-muted-foreground">{member.email}</span>
              </span>

              {member.role === 'owner' ? (
                <span className="text-sm font-semibold" title={t('ownerLocked')}>
                  {roleLabel('owner', language)}
                </span>
              ) : (
                <select
                  aria-label={`${t('role')} — ${member.email}`}
                  value={member.role}
                  onChange={(event) => void changeRole(member, event.target.value as InvitableRole)}
                  className="rounded-lg border px-2 py-1 text-base"
                >
                  {INVITABLE_ROLES.map((option) => (
                    <option key={option} value={option}>
                      {roleLabel(option, language)}
                    </option>
                  ))}
                </select>
              )}

              <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  aria-hidden
                  className={cn('size-2 rounded-full', STATUS_TONE[member.status])}
                />
                {t(STATUS_KEY[member.status])}
              </span>

              {member.role !== 'owner' && (
                <span className="flex shrink-0 gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label={`${t('editMember')} — ${member.email}`}
                    onClick={() => {
                      setDialogError(null);
                      setEditing(member);
                    }}
                  >
                    <Pencil className="size-4" aria-hidden />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void setActive(member, member.status !== 'active')}
                  >
                    {t(ACTION_FOR[member.status])}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => void drop(member)}>
                    {t('remove')}
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {inviting && (
        <MemberDialog
          title={t('inviteTitle')}
          submitLabel={t('send')}
          initial={BLANK_DRAFT}
          busy={busy}
          error={dialogError}
          onClose={() => {
            setInviting(false);
            setDialogError(null);
          }}
          onSubmit={(draft) => void invite(draft)}
        />
      )}

      {editing !== null && (
        <MemberDialog
          title={t('editMember')}
          submitLabel={t('save')}
          initial={draftOf(editing)}
          emailLocked={editing.status !== 'invited'}
          note={t(editing.status === 'invited' ? 'emailResends' : 'emailLocked')}
          busy={busy}
          error={dialogError}
          onClose={() => {
            setEditing(null);
            setDialogError(null);
          }}
          onSubmit={(draft) => void correct(editing, draft)}
        />
      )}
      <Toasts toasts={toasts} undoLabel={t('undo')} />
    </section>
  );
}
